"use client";
import { useState, useEffect, useMemo, useRef } from "react";
import {
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  ComposedChart,
  ReferenceArea,
  ResponsiveContainer,
} from "recharts";

import { Button } from "../chartUi/button";
import { CardTitle } from "../chartUi/card";

import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "../chartUi/chart";

import { DictionaryProps } from "@/types";
import * as S from "./styles";

type DataPoint = Record<string, any>;

type ZoomableChartProps = {
  data?: DataPoint[];
  titleChart: string;
  xAxisDataKey: string;
  yAxisDataKeys: string[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  tooltipNameKey?: string;
  dict?: DictionaryProps["dict"];
  chartConfig: ChartConfig;
  heightFull?: boolean;
};

export function ZoomableChartMultiple({
  data: initialData,
  titleChart,
  xAxisDataKey,
  yAxisDataKeys,
  xAxisLabel,
  yAxisLabel,
  tooltipNameKey,
  dict,
  chartConfig,
  heightFull = false,
}: ZoomableChartProps) {
  const [data, setData] = useState<DataPoint[]>(initialData || []);
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  const [originalData, setOriginalData] = useState<DataPoint[]>(
    initialData || [],
  );
  const [isSelecting, setIsSelecting] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialData?.length) {
      setData(initialData);
      setOriginalData(initialData);
      setStartTime(initialData[0]?.[xAxisDataKey]);
      setEndTime(initialData[initialData.length - 1]?.[xAxisDataKey]);
    }
  }, [initialData, xAxisDataKey]);

  const zoomedData = useMemo(() => {
    if (!startTime || !endTime || !xAxisDataKey) return data;

    const isDate = (val: any) => !isNaN(new Date(val).getTime());

    if (isDate(startTime) && isDate(endTime)) {
      const start = new Date(startTime).getTime();
      const end = new Date(endTime).getTime();
      const filtered = originalData.filter((point) => {
        const time = new Date(point[xAxisDataKey]).getTime();
        return time >= start && time <= end;
      });
      return filtered.length > 1 ? filtered : originalData.slice(0, 2);
    } else {
      const startIdx = originalData.findIndex(
        (d) => d[xAxisDataKey] === startTime,
      );
      const endIdx = originalData.findIndex((d) => d[xAxisDataKey] === endTime);
      if (startIdx !== -1 && endIdx !== -1) {
        const [from, to] = [startIdx, endIdx].sort((a, b) => a - b);
        const filtered = originalData.slice(from, to + 1);
        return filtered.length > 1 ? filtered : originalData.slice(0, 2);
      }
      return originalData;
    }
  }, [startTime, endTime, originalData, data, xAxisDataKey]);

  const total = useMemo(() => {
    return yAxisDataKeys.reduce((acc, key) => {
      const sum = zoomedData.reduce((a, d) => a + (Number(d[key]) || 0), 0);
      return acc + sum;
    }, 0);
  }, [zoomedData, yAxisDataKeys]);

  const handleMouseDown = (e: any) => {
    if (e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
      setIsSelecting(true);
    }
  };

  const handleMouseMove = (e: any) => {
    if (isSelecting && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
    }
  };

  const handleMouseUp = () => {
    if (refAreaLeft && refAreaRight) {
      const [left, right] = [refAreaLeft, refAreaRight].sort();
      setStartTime(left);
      setEndTime(right);
    }
    setRefAreaLeft(null);
    setRefAreaRight(null);
    setIsSelecting(false);
  };

  const handleReset = () => {
    if (originalData.length > 0) {
      setStartTime(originalData[0]?.[xAxisDataKey]);
      setEndTime(originalData[originalData.length - 1]?.[xAxisDataKey]);
    }
  };

  const handleZoom = (
    e: React.WheelEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    //e.preventDefault();
    if (!originalData.length || !chartRef.current || !startTime || !endTime)
      return;

    const isDate = (val: any) => !isNaN(new Date(val).getTime());
    if (!isDate(startTime) || !isDate(endTime)) return;

    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;

    const zoomFactor = 0.1;
    let direction = 0;
    let clientX = 0;

    if ("deltaY" in e) {
      direction = e.deltaY < 0 ? 1 : -1;
      clientX = e.clientX;
    } else if ("touches" in e && e.touches.length === 2) {
      const [t1, t2] = [e.touches[0], e.touches[1]];
      const distance = Math.hypot(
        t1.clientX - t2.clientX,
        t1.clientY - t2.clientY,
      );
      if ((e as any).lastTouchDistance) {
        direction = distance > (e as any).lastTouchDistance ? 1 : -1;
      }
      (e as any).lastTouchDistance = distance;
      clientX = (t1.clientX + t2.clientX) / 2;
    } else return;

    const range = endDate.getTime() - startDate.getTime();
    const zoomAmount = range * zoomFactor * direction;

    const rect = chartRef.current.getBoundingClientRect();
    const mouseX = clientX - rect.left;
    const chartWidth = rect.width;
    const mousePercentage = mouseX / chartWidth;

    const newStart = new Date(
      startDate.getTime() + zoomAmount * mousePercentage,
    );
    const newEnd = new Date(
      endDate.getTime() - zoomAmount * (1 - mousePercentage),
    );

    if (newEnd <= newStart) return;

    setStartTime(newStart.toISOString());
    setEndTime(newEnd.toISOString());
  };

  // Adiciona suporte a zoom com a roda do mouse sem afetar o scroll da página
  useEffect(() => {
    const chartDiv = chartRef.current;
    if (!chartDiv) return;

    const wheelHandler = (e: WheelEvent) => {
      e.preventDefault();
      handleZoom(e as any);
    };

    chartDiv.addEventListener("wheel", wheelHandler, { passive: false });

    return () => {
      chartDiv.removeEventListener("wheel", wheelHandler);
    };
  }, [handleZoom]);

  const formatXAxis = (tickItem: any) => {
    if (
      typeof tickItem === "string" &&
      (tickItem.includes("T") || tickItem.includes("-"))
    ) {
      const date = new Date(tickItem);
      return !isNaN(date.getTime())
        ? date.toLocaleDateString()
        : String(tickItem);
    }
    return String(tickItem);
  };

  return (
    <S.CardChart className="w-full h-full">
      <S.ChartHeader className="flex items-stretch border-b p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1">
          <CardTitle>{titleChart}</CardTitle>
        </div>
        {/* <div className="flex">
                    <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 text-left">
                        <span className="text-xs text-muted-foreground">{dict?.Total}</span>
                        <span className="text-lg font-bold leading-none sm:text-3xl">
                            {total.toLocaleString()}
                        </span>
                    </div>
                </div> */}
      </S.ChartHeader>
      <S.ChartContainer className="px-2 sm:p-6 h-full sm:h-[calc(100%-150px)]">
        <ChartContainer config={chartConfig} className="w-full h-full">
          <div
            className="h-full"
            onTouchMove={handleZoom}
            ref={chartRef}
            style={{ touchAction: "none" }}
          >
            {/* <div className="flex justify-end my-2 sm:mb-4">
                            <Button variant="outline" onClick={handleReset} disabled={!startTime && !endTime} className="text-xs sm:text-sm">
                                Reset
                            </Button>
                        </div> */}
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={zoomedData}
                margin={{
                  top: 10,
                  right: 10,
                  left: 0,
                  bottom: 0,
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey={xAxisDataKey}
                  tickFormatter={formatXAxis}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  minTickGap={16}
                  style={{
                    fontSize: "10px",
                    userSelect: "none",
                  }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  style={{
                    fontSize: "10px",
                    userSelect: "none",
                  }}
                  width={30}
                  label={{
                    angle: -90,
                    position: "insideLeft",
                    fontSize: 12,
                  }}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      className="w-[150px] sm:w-[200px] font-mono text-xs sm:text-sm"
                      nameKey={tooltipNameKey}
                      labelFormatter={(value) => {
                        if (
                          typeof value === "string" &&
                          (value.includes("T") || value.includes("-"))
                        ) {
                          const date = new Date(value);
                          if (!isNaN(date.getTime())) {
                            return date.toLocaleString();
                          }
                        }
                        return String(value);
                      }}
                      formatter={(value: any, name: string | number) => {
                        const label = tooltipNameKey || String(name);
                        return [`${value} `, label];
                      }}
                    />
                  }
                />
                <ChartLegend content={<ChartLegendContent />} />
                {yAxisDataKeys.map((key) => {
                  const color = chartConfig[key]?.color || "#8884d8";
                  return (
                    <Bar
                      key={key}
                      dataKey={key}
                      fill={color}
                      isAnimationActive={false}
                      barSize={24}
                    />
                  );
                })}
                {refAreaLeft && refAreaRight && (
                  <ReferenceArea
                    x1={refAreaLeft}
                    x2={refAreaRight}
                    strokeOpacity={0.3}
                    fill="hsl(var(--foreground))"
                    fillOpacity={0.05}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </ChartContainer>
      </S.ChartContainer>
    </S.CardChart>
  );
}
